/**
 * @fileoverview
 * Error Interceptor - Handles HTTP errors globally
 * Processes standardized API error responses
 * 
 * @version 2.0.0
 * @author KATALYST TEAM
 */

import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { TokenService } from '../services';
import { NotificationService } from '@core/services';
import { ApiErrorResponse, isErrorResponse } from '@core/http/interfaces/api-response.interface';

// ✅ Module-level flag — shared across ALL concurrent requests
let isRedirectingToLogin = false;

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const tokenService = inject(TokenService);
  const notificationService = inject(NotificationService);
  const isReportsExport = req.url.includes('/reports/export');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Extract standardized error information
      let errorMessage = 'An error occurred';
      let errorCode: string | undefined;
      let errorDetails: any = null;

      // Check if error follows standardized format
      if (error.error && isErrorResponse(error.error)) {
        const apiError = error.error as ApiErrorResponse;
        errorMessage = apiError.message;
        errorCode = apiError.error?.code;
        errorDetails = apiError.error?.details;
      } else if (error.error?.message) {
        // Fallback for non-standardized errors
        errorMessage = Array.isArray(error.error.message)
          ? error.error.message.join(', ')
          : error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Handle different error status codes
      switch (error.status) {
        case 401:
          tokenService.removeTokens();
          if (!isRedirectingToLogin) {
            isRedirectingToLogin = true;

            const currentPath = window.location.pathname;

            if (!currentPath.startsWith('/auth/login')) {
              // ✅ Hard redirect — cannot be cancelled by guards or lazy loading
              const returnUrl = encodeURIComponent(currentPath);
              window.location.href = `/auth/login?returnUrl=${returnUrl}&reason=session_expired`;
            } else {
              // Already on login, just reset the flag
              isRedirectingToLogin = false;
            }
          }
          break;

        case 403:
          // Forbidden - insufficient permissions, branch access, or validation failure
          console.error('Forbidden:', errorMessage);
          if (!isReportsExport) {
            notificationService.error(errorMessage, 'Access Denied');
          }
          break;

        case 404:
          // Not found
          console.error('Resource not found:', errorMessage);
          if (!isReportsExport) {
            notificationService.error(errorMessage, 'Not Found');
          }
          break;

        case 422:
          // Validation error - don't show toast, let forms handle field-specific errors
          console.error('Validation error:', errorMessage, errorDetails);
          // Validation errors should be handled by form components to show field-specific messages
          break;

        case 409:
          // Conflict
          console.error('Conflict:', errorMessage);
          if (!isReportsExport) {
            notificationService.error(errorMessage, 'Conflict');
          }

          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          console.error('Server error:', errorMessage);
          if (!isReportsExport) {
            notificationService.error(errorMessage, 'Server Error');
          }
          break;

        case 0:
          // Network error or CORS issue
          console.error('Network error - please check your connection');
          errorMessage = 'Network error - please check your connection';
          if (!isReportsExport) {
            notificationService.error(errorMessage, 'Connection Error');
          }
          break;

        default:
          console.error('HTTP Error:', errorMessage, error);
          // Show error toast for other errors (except 401/403 which redirect)
          if (!isReportsExport && error.status !== 401 && error.status !== 403) {
            notificationService.error(errorMessage, 'Error');
          }
      }

      // Enhance error object with standardized information
      const enhancedError = {
        ...error,
        // Add standardized error information
        apiError: error.error && isErrorResponse(error.error) ? error.error : null,
        errorMessage,
        errorCode,
        errorDetails,
      };

      // Return the enhanced error to be handled by the calling code
      return throwError(() => enhancedError);
    })
  );
};

